import { COLORS } from '@app/configurations';
import {
  AllMetrics,
  CLICK_THROUGH_RATE,
  CLICK_THROUGH_RATE_CLICKS,
  CONVERSION_RATE,
  CONVERSION_RATE_ADJUSTED,
  CONVERSION_RATE_CLICKS,
  CONVERSIONS,
  CONVERSIONS_ADJUSTED,
  CONVERSIONS_CLICKS,
  COST,
  COST_PER_CONVERSION,
  COST_PER_CONVERSION_ADJUSTED,
  COST_PER_CONVERSION_CLICKS,
  COST_REVENUE_RATIO,
  COST_REVENUE_RATIO_ADJUSTED,
  COST_REVENUE_RATIO_CLICKS,
  IMPRESSIONS,
  REVENUE,
  REVENUE_ADJUSTED,
  REVENUE_CLICKS,
  ROAS,
  ROAS_ADJUSTED,
  ROAS_CLICKS,
} from '@app/core/models/metrics.constants';
import * as Highcharts from 'highcharts';
import { DashStyleValue, XAxisOptions } from 'highcharts';

export interface ChartEvent<T = any> {
  type: ChartEventType;
  data?: T;
  fromDefault?: boolean;
}

export const MAX_CHART_LINES = 3;

export type ChartEventType = 'ITEM_SELECT' | 'FREQUENCY_CHANGE';

export enum ChartFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
}

export enum ChartView {
  SPLIT = 'split',
  COMPARE = 'compare',
}

export const CHART_CONSTANTS = {
  FREQUENCY: ChartFrequency,
  CHART_TYPES: {
    SHARP: 'sharp',
    SMOOTH: 'smooth',
  },
};

export const ExportMenu = [{ key: 'csv' }, { key: 'xls' }] as {
  key: string;
  name: string;
}[];

export const ChartResetDefaults = {
  accessibility: { enabled: false },
  legend: { enabled: false },
  credits: { enabled: false },
  exporting: { enabled: false },
  title: { text: '' },
  plotOptions: { series: { animation: false }, line: { animation: false } },
};

export const CHART_COLORS = {
  grindLines: '#E4E8F0',
  axisLineColor: COLORS.SECONDARY_TEXT,
};

export const CHART_SCALE_WIDTH = 55;
export const CHART_CROSSHAIR = { crosshair: { color: COLORS.BRAND_OPACITY_20 } };

export const AxisGridInfo = {
  gridLineWidth: 1,
  gridLineColor: CHART_COLORS.grindLines,
  gridLineDashStyle: 'Solid' as DashStyleValue,
};

const xAxis: Omit<XAxisOptions, 'categories'> = {
  ...AxisGridInfo,
  ...(<XAxisOptions>{ style: { color: CHART_COLORS.axisLineColor } }),
  lineWidth: 1,
  lineColor: CHART_COLORS.axisLineColor,
  ...CHART_CROSSHAIR,
  title: { text: null },
  labels: {
    rotation: 0,
    style: { whiteSpace: 'nowrap', textOverflow: 'none' },
  },
};

export const BasicChartOptions: Highcharts.Options = {
  ...ChartResetDefaults,
  plotOptions: {
    series: {
      animation: false,
      states: {
        inactive: {
          opacity: 1,
        },
      },
    },
  },
  xAxis: { ...xAxis },
  chart: {
    animation: false,
    marginTop: 5,
    marginBottom: 40,
    marginLeft: CHART_SCALE_WIDTH,
    marginRight: CHART_SCALE_WIDTH * 2,
  },
  tooltip: { shared: true, useHTML: true, enabled: true, outside: true },
};

export interface ChartMarker {
  color: string;
  radius: number;
  fadeColor: string;
  symbol: 'circle' | 'square' | 'diamond' | 'triangle' | 'triangle-down';
}

export const ChartMarkers: ChartMarker[] = [
  { radius: 4, symbol: 'circle', color: COLORS.PINK, fadeColor: '#DDA7C7' },
  { radius: 4, symbol: 'square', color: COLORS.ORANGE, fadeColor: '#FFD380' },
  { radius: 5, symbol: 'diamond', color: COLORS.PURPLE, fadeColor: '#ABA7C6' },
  { radius: 5, symbol: 'triangle', color: COLORS.RED, fadeColor: COLORS.RED },
  { radius: 5, symbol: 'triangle-down', color: COLORS.BLUE, fadeColor: COLORS.BLUE },
];

export interface ChartMetricConfig {
  visible: boolean;
  enable: boolean;
  id: AllMetrics;
  index: number;
  marker: ChartMarker;
}

export enum ChartNotifications {
  INIT = 'INIT',
  ADD = 'ADD',
  REMOVE = 'REMOVE',
  SPOTLIGHT = 'SPOTLIGHT',
  FREQUENCY = 'FREQUENCY',
  COA = 'COA',
}

export interface AxisValues {
  isAmzPartial: boolean;
  date?: Date;
  name: string;
  isPartial: boolean;
  start?: string;
  end?: string;
}

export const FrequencyOptions: ChartFrequency[] = [
  ChartFrequency.DAILY,
  ChartFrequency.WEEKLY,
  ChartFrequency.MONTHLY,
  ChartFrequency.QUARTERLY,
];

export const DefaultMetrics: AllMetrics[] = [COST, CONVERSIONS, IMPRESSIONS];

export interface ChartData {
  date: string;
  [key: string]: any;
}

export const COAColumns = {
  [CONVERSIONS]: CONVERSIONS_CLICKS,
  [REVENUE]: REVENUE_CLICKS,
  [CONVERSION_RATE]: CONVERSION_RATE_CLICKS,
  [CLICK_THROUGH_RATE]: CLICK_THROUGH_RATE_CLICKS,
  [COST_PER_CONVERSION]: COST_PER_CONVERSION_CLICKS,
  [ROAS]: ROAS_CLICKS,
  [COST_REVENUE_RATIO]: COST_REVENUE_RATIO_CLICKS,
  [REVENUE_ADJUSTED]: REVENUE_CLICKS,
  [CONVERSIONS_ADJUSTED]: CONVERSIONS_CLICKS,
  [CONVERSION_RATE_ADJUSTED]: CONVERSION_RATE_CLICKS,
  [ROAS_ADJUSTED]: ROAS_CLICKS,
  [COST_REVENUE_RATIO_ADJUSTED]: COST_REVENUE_RATIO_CLICKS,
  [COST_PER_CONVERSION_ADJUSTED]: COST_PER_CONVERSION_CLICKS,
};
